import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, EmptyState, ListSkeleton } from "@/components/common";
import { TASK_STATUSES, PRIORITIES, item } from "@/lib/constants";
import { fmtDate, isOverdue } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import TaskDialog from "@/components/TaskDialog";
import { CheckSquare } from "lucide-react";

function TaskRow({ t, onClick }) {
  const st = item(TASK_STATUSES, t.status);
  const pr = item(PRIORITIES, t.priority);
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted text-left transition-colors border" data-testid={`mywork-task-${t.id}`}>
      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: st.color }} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{t.title}</p>
        <div className="flex items-center gap-2 mt-1">
          <Badge className={`text-[10px] ${st.chip}`} variant="outline">{st.label}</Badge>
          <Badge className={`text-[10px] ${pr.chip}`} variant="outline">{pr.label}</Badge>
        </div>
      </div>
      {t.due_date && <span className={`text-xs ${isOverdue(t.due_date, t.status) ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>{fmtDate(t.due_date)}</span>}
    </button>
  );
}

export default function MyWork() {
  const [openId, setOpenId] = useState(null);
  const { data = [], isLoading } = useQuery({ queryKey: ["mywork"], queryFn: async () => { const d = (await api.get("/tasks", { params: { mine: true } })).data; return Array.isArray(d) ? d : []; } });

  const open = data.filter((t) => !["completed", "cancelled"].includes(t.status));
  const overdue = open.filter((t) => t.overdue);
  const todayStr = new Date().toISOString().slice(0, 10);
  const today = open.filter((t) => (t.due_date || "").slice(0, 10) === todayStr);
  const done = data.filter((t) => t.status === "completed");

  const List = ({ items }) => items.length ? (
    <div className="space-y-2">{items.map((t) => <TaskRow key={t.id} t={t} onClick={() => setOpenId(t.id)} />)}</div>
  ) : <EmptyState icon={CheckSquare} title="Görev yok" description="Bu listede henüz görev bulunmuyor." />;

  return (
    <div>
      <PageHeader title="İşlerim" subtitle="Size atanan görevler" />
      {isLoading ? <ListSkeleton /> : (
        <Tabs defaultValue="open">
          <TabsList>
            <TabsTrigger value="open" data-testid="mywork-tab-open">Açık ({open.length})</TabsTrigger>
            <TabsTrigger value="today" data-testid="mywork-tab-today">Bugün ({today.length})</TabsTrigger>
            <TabsTrigger value="overdue" data-testid="mywork-tab-overdue">Geciken ({overdue.length})</TabsTrigger>
            <TabsTrigger value="done" data-testid="mywork-tab-done">Tamamlanan ({done.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="open" className="mt-4"><List items={open} /></TabsContent>
          <TabsContent value="today" className="mt-4"><List items={today} /></TabsContent>
          <TabsContent value="overdue" className="mt-4"><List items={overdue} /></TabsContent>
          <TabsContent value="done" className="mt-4"><List items={done} /></TabsContent>
        </Tabs>
      )}
      <TaskDialog open={!!openId} onOpenChange={(v) => !v && setOpenId(null)} taskId={openId} />
    </div>
  );
}
